/**
 * The web column-type registry (spec M1-B5 §6), mirroring `packages/shared`'s by `type`.
 *
 * Adding a type = one folder next to this file plus one line in `registerBuiltInColumnUis()`.
 * Nothing outside a type's folder branches on the type (CLAUDE.md rule 6); the grid, the panes
 * and the gallery all go through `getColumnUi()`.
 */
import { createRegistry, findColumnType, type AnyColumnTypeDefinition } from '@elizmonday/shared';
import { autoNumberColumnUi } from './auto-number/index';
import { boardRelationColumnUi } from './board-relation/index';
import { buttonColumnUi } from './button/index';
import { checkboxColumnUi } from './checkbox/index';
import { countryColumnUi } from './country/index';
import { creationLogColumnUi } from './creation-log/index';
import { dateColumnUi } from './date/index';
import { dependencyColumnUi } from './dependency/index';
import { dropdownColumnUi } from './dropdown/index';
import { emailColumnUi } from './email/index';
import { fileColumnUi } from './file/index';
import { formulaColumnUi } from './formula/index';
import { hourColumnUi } from './hour/index';
import { itemIdColumnUi } from './item-id/index';
import { lastUpdatedColumnUi } from './last-updated/index';
import { linkColumnUi } from './link/index';
import { locationColumnUi } from './location/index';
import { longTextColumnUi } from './long-text/index';
import { mirrorColumnUi } from './mirror/index';
import { nameColumnUi } from './name/index';
import { numbersColumnUi } from './numbers/index';
import { peopleColumnUi } from './people/index';
import { phoneColumnUi } from './phone/index';
import { progressColumnUi } from './progress/index';
import { ratingColumnUi } from './rating/index';
import { personColumnUi } from './person/index';
import { statusColumnUi } from './status/index';
import { subtasksColumnUi } from './subtasks/index';
import { tagsColumnUi } from './tags/index';
import { textColumnUi } from './text/index';
import { timeTrackingColumnUi } from './time-tracking/index';
import { timelineColumnUi } from './timeline/index';
import { unsupportedColumnUi } from './unsupported/index';
import { worldClockColumnUi } from './world-clock/index';
import type { AnyColumnTypeUi } from './types';

export const columnUiRegistry = createRegistry<AnyColumnTypeUi>(
  'column-ui-registry',
  (definition) => definition.type,
);

/** monday's second spelling of the deprecated single-person column `[09§A.7 deprecated]`. */
const ALIASES: Readonly<Record<string, string>> = { item_assignees: 'person' };

let registered = false;

export function registerBuiltInColumnUis(): void {
  if (registered) return;
  registered = true;
  for (const definition of [
    nameColumnUi,
    statusColumnUi,
    textColumnUi,
    longTextColumnUi,
    numbersColumnUi,
    dateColumnUi,
    peopleColumnUi,
    personColumnUi,
    dropdownColumnUi,
    subtasksColumnUi,
    lastUpdatedColumnUi,
    creationLogColumnUi,
    checkboxColumnUi,
    // Tier 2 / 3 (`specs/M2-tier2-column-types.md`), in the account's usage order.
    linkColumnUi,
    tagsColumnUi,
    timelineColumnUi,
    fileColumnUi,
    locationColumnUi,
    worldClockColumnUi,
    timeTrackingColumnUi,
    phoneColumnUi,
    emailColumnUi,
    ratingColumnUi,
    hourColumnUi,
    countryColumnUi,
    itemIdColumnUi,
    autoNumberColumnUi,
    progressColumnUi,
    buttonColumnUi,
    formulaColumnUi,
    boardRelationColumnUi,
    mirrorColumnUi,
    dependencyColumnUi,
    unsupportedColumnUi,
  ] as AnyColumnTypeUi[]) {
    columnUiRegistry.register(definition);
  }
}

registerBuiltInColumnUis();

/** R1: an unregistered type resolves to `unsupported`, which never loses data. */
export function getColumnUi(type: string): AnyColumnTypeUi {
  return columnUiRegistry.find(ALIASES[type] ?? type) ?? unsupportedColumnUi;
}

export function hasColumnUi(type: string): boolean {
  return columnUiRegistry.has(ALIASES[type] ?? type);
}

/** Memoised per raw settings object: the grid re-renders a cell far more often than a board loads. */
const NORMALISED_SETTINGS = new WeakMap<object, unknown>();

/**
 * **One shape, decided here** — `BoardColumn.settings` is "canonical (decoded `settings_str`)"
 * (`../data/types.ts`), and this is what makes that true whatever the source handed us.
 *
 * monday serialises several types **twice**: `status`'s labels, and — the one that crashed the
 * board route in round 5t — `mirror.displayed_linked_columns`, which is an object in
 * `settings_str` (`{"5939123543":["status1"]}`) and an array in `Column.settings`
 * (`[{board_id, column_ids}]`). Both are verbatim in the snapshot (T-5r), the board fixture
 * holds the **array** form, and the web mirror editor read it as the map: `columnIds.map(...)`
 * over `{board_id, column_ids}` threw `t.map is not a function`, React Router caught it, and
 * the whole `/__ui/board` route was replaced by *Something went wrong · Retry*.
 *
 * Round 5q made the shared `mirror` helpers accept either form; this closes the same hole for
 * **every** type and every consumer at once (renderer, editor, settings dialog, filters,
 * group-by, footer, clipboard) rather than per call site: the type's own `settingsSchema` is
 * the single normaliser, exactly as the api uses it. CLAUDE.md rule 6 — no call site branches
 * on the type.
 *
 * Settings that do **not** parse are passed through untouched. A board must render with
 * settings we have not modelled yet; the type's UI already tolerates `unknown`, and dropping
 * them would lose data.
 */
export function normaliseColumnSettings(type: string, settings: unknown): unknown {
  if (settings === null || typeof settings !== 'object') return settings;
  const cached = NORMALISED_SETTINGS.get(settings);
  if (cached !== undefined) return cached;
  const shared = findColumnType(type) ?? findColumnType(ALIASES[type] ?? type);
  const next = shared === undefined ? settings : (normaliseWith(shared, settings) ?? settings);
  NORMALISED_SETTINGS.set(settings, next);
  return next;
}

/**
 * `fromMondaySettings` **first**, `settingsSchema` only as the fallback (round 6b).
 *
 * `settingsSchema` describes the canonical `settings_str` and nothing else, so it *rejects*
 * monday's other serialisation instead of converting it — and a rejected parse fell through to
 * "pass the raw object on", which is how the array form reached the renderer. `status` is the
 * case that crashed `/boards/1000000000000` with React error #31: its `settings_str.labels` is
 * `{"0": "Working on it"}` canonically but `[{id, color, index, label, is_done}]` in
 * `Column.settings`, `settingsSchema`'s `z.record(string, string)` rejects the array, and
 * `findLabelById(settings, 0)` then returned `labels["0"]` — **the label object** — which the
 * cell rendered as a React child and React refuses to render.
 *
 * Every type's `fromMondaySettings` is the one function that already accepts both
 * serialisations (it is what the api and the importer normalise with), so using it here keeps
 * one normaliser for the whole client and needs no per-type branch (CLAUDE.md rule 6).
 * Settings that neither path can read are still passed through untouched: a board must render
 * with settings we have not modelled, and dropping them would lose data.
 */
type SettingsNormaliser = Pick<AnyColumnTypeDefinition, 'fromMondaySettings' | 'settingsSchema'>;

function normaliseWith(shared: SettingsNormaliser, settings: object): unknown {
  try {
    return shared.fromMondaySettings(settings);
  } catch {
    const parsed = shared.settingsSchema.safeParse(settings);
    return parsed.success ? parsed.data : settings;
  }
}

/** The add-column gallery's entries: every registered type except the fallback (H5). */
export function galleryEntries(): {
  type: string;
  title: string;
  description: string;
  group: AnyColumnTypeUi['gallery']['group'];
  keywords: readonly string[];
  icon: AnyColumnTypeUi['icon'];
}[] {
  return columnUiRegistry
    .list()
    .filter((definition) => definition.type !== 'unsupported')
    .map((definition) => {
      const shared = findColumnType(definition.type);
      return {
        type: definition.type,
        title: shared?.title ?? definition.type,
        description: shared?.description ?? '',
        group: definition.gallery.group,
        keywords: definition.gallery.keywords,
        icon: definition.icon,
      };
    });
}

/**
 * The width a column **occupies**: `columns.width`, else the type's default `[B4§8.1]`.
 *
 * For `name` this is monday's sticky *region* (P-021), not the cell — `table/layout.ts`'s
 * `nameCellWidth()` is what turns it into a box.
 */
export function columnWidth(column: { type: string; width: number | null }): number {
  return column.width ?? getColumnUi(column.type).defaultWidth;
}
